import { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";
import PageBreadcrumb from "../../../components/common/PageBreadCrumb";
import VerificationStatus from "../../../components/session/VerificationStatus";
import authorizedApi from "../../../api/axiosAuthorizedInstance";
import toast from "react-hot-toast";
import Badge from "../../../components/ui/badge/Badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { FaVideo, FaVideoSlash } from "react-icons/fa";
import { BsFillMicFill, BsFillMicMuteFill } from "react-icons/bs";
import { MdOutlineCameraswitch } from "react-icons/md";
import Modal from "../../../components/common/Modal";
import { connectSocket } from "../../../lib/socket";
import { ThreeDots } from 'react-loader-spinner';

import {
  ConsoleLogger,
  DefaultDeviceController,
  DefaultMeetingSession,
  LogLevel,
  MeetingSessionConfiguration,
} from "amazon-chime-sdk-js";

export default function RegistrationDetail() {
  const apiUrl = import.meta.env.VITE_API_URL;
  const contentUrl = import.meta.env.VITE_CONTENT_URL;
  const videoContentUrl = import.meta.env.VITE_VIDEO_CONTENT_URL;

  const { sessionId } = useParams();
  const [meetingId, setMeetingId] = useState(null);
  const [data, setData] = useState({});
  const [meetings, setMeetings] = useState([]);

  const audioRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteContainerRef = useRef(null);

  const meetingSessionRef = useRef(null);
  const audioBoundRef = useRef(false);
  const audioVideoObserverRef = useRef(null);

  const [joined, setJoined] = useState(false);
  const [joining, setJoining] = useState(false);
  const [muted, setMuted] = useState(false);
  const [videoOn, setVideoOn] = useState(true);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState("");

  const videoDevicesRef = useRef([]);
  const currentCameraIndexRef = useRef(0);

  function tryBindAudio() {
    const meetingSession = meetingSessionRef.current;
    const audioEl = audioRef.current;

    if (!meetingSession || !audioEl || audioBoundRef.current) return;

    meetingSession.audioVideo.bindAudioElement(audioEl);
    audioBoundRef.current = true;
  }

  const switchCamera = async () => {
    const meetingSession = meetingSessionRef.current;
    if (!meetingSession) return;

    const audioVideo = meetingSession.audioVideo;

    const devices = videoDevicesRef.current;

    if (!devices || devices.length <= 1) return;

    const next =
      (currentCameraIndexRef.current + 1) % devices.length;

    await audioVideo.startVideoInput(devices[next].deviceId);

    currentCameraIndexRef.current = next;
  };

  useEffect(() => {
    const fetchData = async () => {
      authorizedApi
        .get(apiUrl + "/api/v1/sessions/detail/" + sessionId)
        .then((response) => {
          setData(response.data);
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
        });
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchMeetingData = async () => {
      authorizedApi
        .get(
          apiUrl +
            "/api/v1/meetings/by_session/completed?sessionId=" +
            sessionId,
        )
        .then((response) => {
          setMeetings(response.data);
        })
        .catch((error) => {
          console.error("Error fetching meeting data:", error);
        });
    };

    fetchMeetingData();
  }, []);

  useEffect(() => {
    connectSocket((event) => {

    console.log(event)

      setMeetings(prev =>
  prev.map(m =>
    m.meetingId === event.meetingId
      ? { ...m, videoStatus: event.videoStatus }
      : m
  )
)
  })
}, [])

  const print = (s) => {
    if (s == null || s.toString().length == 0) {
      s = "n/a";
    }

    return s;
  };

  const openModalWithContent = (meetingId) => {
    const videoUrl =
      videoContentUrl + "/" +
      meetingId +
      "/final.mp4";
    setModalContent(
      '<video controls width="800"> <source src="' +
        videoUrl +
        '" type="video/mp4" />Your browser does not support the video tag.</video>',
    );
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setModalContent("");
  };

  const startMeeting = async () => {
    if (joining || joined) return;
    setJoining(true);

    try {
      const response = await authorizedApi.post(
        apiUrl + "/api/v1/meetings/start?sessionId=" + sessionId,
      );

      setMeetingId(response.data.meeting?.meetingId);

      const logger = new ConsoleLogger("Chime", LogLevel.INFO);
      const deviceController = new DefaultDeviceController(logger);

      const configuration = new MeetingSessionConfiguration(
        response.data.meeting,
        response.data.attendee,
      );

      const meetingSession = new DefaultMeetingSession(
        configuration,
        logger,
        deviceController,
      );

      meetingSessionRef.current = meetingSession;
      const audioVideo = meetingSession.audioVideo;

      const audioInputs = await audioVideo.listAudioInputDevices();
      const videoInputs = await audioVideo.listVideoInputDevices();

      videoDevicesRef.current = videoInputs;
      currentCameraIndexRef.current = 0;

      if (audioInputs.length) {
        await audioVideo.startAudioInput(audioInputs[0].deviceId);
      }
      if (videoInputs.length) {
        await audioVideo.startVideoInput(videoInputs[0].deviceId);
      }

      // Video observers
      const observer = {
        videoTileDidUpdate: (tileState) => {
          if (!tileState.boundAttendeeId) return;

          // Local video
          if (tileState.localTile) {
            if (localVideoRef.current) {
              audioVideo.bindVideoElement(
                tileState.tileId,
                localVideoRef.current,
              );
            }
            return;
          }

          // Prevent duplicate tiles
          const existingVideo = remoteContainerRef.current?.querySelector(
            `video[data-tile-id="${tileState.tileId}"]`,
          );

          if (existingVideo) {
            // Just rebind, DO NOT create new element
            audioVideo.bindVideoElement(tileState.tileId, existingVideo);
            return;
          }

          // Create video ONLY ONCE
          const video = document.createElement("video");
          video.autoplay = true;
          video.playsInline = true;
          video.dataset.tileId = tileState.tileId;
          video.className = "w-full h-full object-cover rounded-xl bg-black";

          audioVideo.bindVideoElement(tileState.tileId, video);
          remoteContainerRef.current?.appendChild(video);
        },

        videoTileWasRemoved: (tileId) => {
          const video = remoteContainerRef.current?.querySelector(
            `video[data-tile-id="${tileId}"]`,
          );
          video?.remove();
        },
      };

      audioVideoObserverRef.current = observer;
      audioVideo.addObserver(observer);

      // Start session
      audioVideo.start();
      audioVideo.startLocalVideoTile();

      setJoined(true);
      tryBindAudio();
    } catch (err) {
      console.error("Failed to start meeting", err);
    } finally {
      setJoining(false);
    }
  };

  /* ---------------------------------------------
     LEAVE MEETING
  --------------------------------------------- */
  async function leaveMeeting() {
    const meetingSession = meetingSessionRef.current;
    if (!meetingSession) return;

    if (!confirm("Aye you sure, you want to leave this meeting?")) {
      return;
    }

    const audioVideo = meetingSession.audioVideo;

    try {
      audioVideo.stopLocalVideoTile();

      await audioVideo.stopVideoInput();

      await audioVideo.stopAudioInput();

      audioVideo.stop();

      // Remove observer
      if (audioVideoObserverRef.current) {
        audioVideo.removeObserver(audioVideoObserverRef.current);
        audioVideoObserverRef.current = null;
      }

      // Cleanup DOM
      remoteContainerRef.current.innerHTML = "";

      // Reset refs
      meetingSessionRef.current = null;
      audioBoundRef.current = false;

      // Reset UI state
      setJoined(false);
      setMuted(false);
      setVideoOn(true);

      authorizedApi
        .post(apiUrl + "/api/v1/meetings/end?meetingId=" + meetingId)
        .then((response) => {
          setMeetings((prevItems) => [...prevItems, response.data.data]);
          console.log(response.data.message);
        })
        .catch((error) => {
          toast.error(error.response.data.message);
        });
    } catch (err) {
      console.error("Error while leaving meeting:", err);
    }
  }

  /* ---------------------------------------------
     CONTROLS
  --------------------------------------------- */
  function toggleMute() {
    const audioVideo = meetingSessionRef.current?.audioVideo;
    if (!audioVideo) return;

    muted
      ? audioVideo.realtimeUnmuteLocalAudio()
      : audioVideo.realtimeMuteLocalAudio();

    setMuted(!muted);
  }

  async function toggleVideo() {
    const audioVideo = meetingSessionRef.current?.audioVideo;
    if (!audioVideo) return;

    if (videoOn) {
      audioVideo.stopLocalVideoTile();
      await audioVideo.stopVideoInput();
    } else {
      const devices = await audioVideo.listVideoInputDevices();
      if (!devices.length) return;

      await audioVideo.startVideoInput(devices[0].deviceId);
      audioVideo.startLocalVideoTile();
    }

    setVideoOn(v => !v);
  }

  /* ---------------------------------------------
     SAFE AUDIO BIND AFTER EVERY RENDER
  --------------------------------------------- */
  useEffect(() => {
    tryBindAudio();
  });

  /* ---------------------------------------------
     CLEANUP ON UNMOUNT
  --------------------------------------------- */
  useEffect(() => {
    return () => leaveMeeting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Helmet>
        <title>Registration Detail</title>
      </Helmet>

      <PageBreadcrumb pageTitle="Registration Details" />

      <div className="p-5 mb-6 border border-gray-200 rounded-2xl dark:border-gray-800 lg:p-6">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-col items-center w-full gap-6 xl:flex-row">
            <div className="w-20 h-20 overflow-hidden border border-gray-200 rounded-full dark:border-gray-800">
              <img src={contentUrl + data.imageUrl} alt="user" />
            </div>
            <div className="order-3 xl:order-2">
              <h4 className="mb-2 text-lg font-semibold text-center text-gray-800 dark:text-white/90 xl:text-left">
                {data.firstName} {data.lastName}
              </h4>
              <div className="flex flex-col items-center gap-1 text-center xl:flex-row xl:gap-3 xl:text-left">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {data.email}
                </p>
                <div className="hidden h-3.5 w-px bg-gray-300 dark:bg-gray-700 xl:block"></div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {data.mobile}
                </p>
              </div>
            </div>
            <div className="flex items-center order-2 gap-2 grow xl:order-3 xl:justify-end">
              {!joined && (
                <button
                  type="button"
                  onClick={startMeeting}
                  disabled={joining}
                  className="items-center justify-center px-4 py-2 text-sm font-medium text-white transition rounded-lg bg-brand-500 shadow-theme-xs hover:bg-brand-600"
                >
                  {joining ? "Starting .." : "Start Video Call"}
                </button>
              )}

              {joined && (
                // <div className="w-full relative h-[400px] p-1 rounded">
                <div className="w-full h-[400px] bg-neutral-900 text-white flex flex-col">
                  <audio ref={audioRef} autoPlay />

                  <div className="flex-1 relative bg-neutral-900">
                    {/* Remote videos (full area) */}
                    <div
                      ref={remoteContainerRef}
                      className="absolute inset-0 p-4"
                    />

                    {/* Local video (PiP bottom-right) */}
                    {videoOn && (
                      <div className="absolute bottom-4 right-4 w-38 h-26 md:w-46 md:h-30 bg-black rounded-xl overflow-hidden shadow-2xl border border-neutral-700">
                        <video
                          ref={localVideoRef}
                          autoPlay
                          muted
                          playsInline
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                  </div>

                  {/* Controls */}
                  <div className="flex justify-center gap-4 p-4 bg-neutral-800">
                    <button
                      onClick={toggleMute}
                      className="px-5 py-2 rounded-lg bg-neutral-700 hover:bg-neutral-600"
                    >
                      {muted ? <BsFillMicMuteFill /> : <BsFillMicFill />}
                    </button>

                    <button
                      onClick={toggleVideo}
                      className="px-5 py-2 rounded-lg bg-neutral-700 hover:bg-neutral-600"
                    >
                      {videoOn ? <FaVideo /> : <FaVideoSlash />}
                    </button>

                    <button
                      onClick={switchCamera}
                      className="px-5 py-2 rounded-lg bg-neutral-700 hover:bg-neutral-600"
                    >
                      <MdOutlineCameraswitch />
                    </button>

                    <button
                      onClick={leaveMeeting}
                      className="px-5 py-2 rounded-lg bg-red-600 hover:bg-red-500"
                    >
                      Leave
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {meetings.length > 0 && (
        <div className="p-5 mb-6 border border-gray-200 rounded-2xl dark:border-gray-800 lg:p-6">
          <div className="gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h4 className="text-lg font-semibold text-gray-800 dark:text-white/90 lg:mb-6">
                Video Calls
              </h4>

              <Table>
                <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                  <TableRow>
                    <TableCell
                      isHeader
                      className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                    >
                      Meeting ID
                    </TableCell>
                    <TableCell
                      isHeader
                      className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                    >
                      Started at
                    </TableCell>
                    <TableCell
                      isHeader
                      className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                    >
                      End at
                    </TableCell>
                    <TableCell
                      isHeader
                      className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                    >
                      Video Status
                    </TableCell>

                    <TableCell
                      isHeader
                      className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                    ></TableCell>
                  </TableRow>
                </TableHeader>

                <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                  {meetings.map((meeting) => (
                    <TableRow key={meeting.id}>
                      <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                        {meeting.meetingId}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                        {meeting.startedTime}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                        {meeting.endTime}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                        {meeting.videoStatus == "P" && (
                          <Badge size="sm" color="warning">
                            Pending
                          </Badge>
                        )}
                        {meeting.videoStatus == "A" && (
                          <Badge size="sm" color="success">
                            Available
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="px-4 py-3 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                        {meeting.videoStatus == "P" && (
                          <ThreeDots
          visible={true}
          height="20"
          width="20"
          color="#4fa94d"
          ariaLabel="three-dots-loading"
        />
                        )}

                        {meeting.videoStatus == "A" && (
                        <button
                          type="button"
                          onClick={() =>
                            openModalWithContent(meeting.meetingId)
                          }
                        >
                          watch
                        </button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

        </div>
      )}

      <div className="p-5 mb-6 border border-gray-200 rounded-2xl dark:border-gray-800 lg:p-6">
        <div className="gap-6 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h4 className="text-lg font-semibold text-gray-800 dark:text-white/90 lg:mb-6">
              Personal Information
            </h4>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-4 lg:gap-7 2xl:gap-x-32">
              <div>
                <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                  First Name
                </p>
                <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                  {data.firstName}
                </p>
              </div>

              <div>
                <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                  Last Name
                </p>
                <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                  {data.lastName}
                </p>
              </div>

              <div>
                <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                  Other Names
                </p>
                <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                  {print(data.otherNames)}
                </p>
              </div>

              <div>
                <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                  ID Number
                </p>
                <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                  {data.idNumber}
                </p>
              </div>

              <div>
                <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                  Email address
                </p>
                <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                  {data.email}
                </p>
              </div>

              <div>
                <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                  Mobile
                </p>
                <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                  {data.mobile}
                </p>
              </div>

              <div>
                <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                  Address
                </p>
                <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                  {print(data.address)}
                </p>
              </div>

              <div>
                <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                  City
                </p>
                <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                  {print(data.city)}
                </p>
              </div>

              <div>
                <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                  Date of Birth
                </p>
                <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                  {print(data.dateOfBirth)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-5 mb-6 border border-gray-200 rounded-2xl dark:border-gray-800 lg:p-6">
        <div className="gap-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex flex-row w-full">
            <h4 className="flex-3/4 text-lg font-semibold text-gray-800 dark:text-white/90 lg:mb-6">
              ID Verification
            </h4>

            <div className="flex-1/3 text-right">
              <VerificationStatus status={data.idVerification?.status} />
            </div>
          </div>

          <div className="flex flex-row w-full mr-4">
            <div className="flex-2/3 w-full">
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-2 lg:gap-7 2xl:gap-x-32">
                <div>
                  <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                    Document Type
                  </p>
                  <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                    {print(data.idVerification?.document_type)}
                  </p>
                </div>

                <div>
                  <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                    Document Number
                  </p>
                  <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                    {print(data.idVerification?.document_number)}
                  </p>
                </div>

                <div>
                  <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                    Age
                  </p>
                  <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                    {print(data.idVerification?.age)}
                  </p>
                </div>

                <div>
                  <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                    Date of Birth
                  </p>
                  <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                    {print(data.idVerification?.date_of_birth)}
                  </p>
                </div>

                <div>
                  <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                    Expiration Date
                  </p>
                  <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                    {print(data.idVerification?.expiration_date)}
                  </p>
                </div>

                <div>
                  <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                    Issue Date
                  </p>
                  <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                    {print(data.idVerification?.date_of_issue)}
                  </p>
                </div>

                <div>
                  <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                    First Name
                  </p>
                  <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                    {print(data.idVerification?.first_name)}
                  </p>
                </div>

                <div>
                  <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                    Last Name
                  </p>
                  <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                    {print(data.idVerification?.last_name)}
                  </p>
                </div>

                <div>
                  <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                    Gender
                  </p>
                  <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                    {print(data.idVerification?.gender)}
                  </p>
                </div>

                <div>
                  <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                    Address
                  </p>
                  <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                    {print(data.idVerification?.address)}
                  </p>
                </div>

                <div>
                  <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                    Marital Status
                  </p>
                  <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                    {print(data.idVerification?.marital_status)}
                  </p>
                </div>

                <div>
                  <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                    Nationality
                  </p>
                  <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                    {print(data.idVerification?.nationality)}
                  </p>
                </div>
              </div>

              <div>
                <p className="mt-5 text-xs leading-normal text-gray-500 dark:text-gray-400">
                  Warnings
                </p>
                <div className="text-sm font-medium text-gray-800 dark:text-white/90">
                  {data.idVerification?.warnings.map((w, ndx) => (
                    <p key={ndx}>{w.short_description}</p>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex-1/3 w-full">
              <div className="p-3 mb-4 border border-gray-300 dark:border-white rounded-2xl">
                <img alt="ID Front" src={contentUrl + data.frontImageUrl} />
              </div>

              <div className="p-3 mb-4 border border-gray-300 dark:border-white rounded-2xl">
                <img alt="ID Back" src={contentUrl + data.backImageUrl} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Face match */}

      <div className="p-5 mb-6 border border-gray-200 rounded-2xl dark:border-gray-800 lg:p-6">
        <div className="gap-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex flex-row w-full">
            <h4 className="flex-2/6 text-lg font-semibold text-gray-800 dark:text-white/90 lg:mb-6">
              Face Match
            </h4>

            <div className="flex-2/6 w-full">
              <div className="flex w-full items-center gap-3">
                <div className="relative block h-2 w-full rounded-sm bg-gray-200 dark:bg-gray-800">
                  <div
                    className={`absolute left-0 top-0 flex h-full items-center justify-center rounded-sm bg-brand-500 text-xs font-medium text-white`}
                    style={{ width: data.faceMatch?.score + "%" }}
                  ></div>
                </div>
                <p className="text-theme-sm font-medium text-gray-800 dark:text-white/90">
                  {data.faceMatch?.score}%
                </p>
              </div>
            </div>

            <div className="flex-2/6 text-right">
              <VerificationStatus status={data.faceMatch?.status} />
            </div>
          </div>

          <div className="flex flex-row w-full mr-4">
            <div className="flex-1/3 w-full mr-5">
              <div className="p-3 mb-4 border border-gray-300 dark:border-white rounded-2xl">
                <img alt="ID Front" src={contentUrl + data.portraitImageUrl} />
              </div>
            </div>

            <div className="flex-1/3 w-full mr-5">
              <div className="p-3 mb-4 border border-gray-300 dark:border-white rounded-2xl">
                <img alt="ID Back" src={contentUrl + data.selfieImageUrl} />
              </div>
            </div>

            <div className="flex-1/3 w-full">
              {data.faceMatch?.warnings.length > 0 && (
                <div>
                  <p className="mt-5 text-xs leading-normal text-gray-500 dark:text-gray-400">
                    Warnings
                  </p>
                  <div className="text-sm font-medium text-gray-800 dark:text-white/90">
                    {data.faceMatch?.warnings.map((w, ndx) => (
                      <p key={ndx}>{w.short_description}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Liveness */}

      <div className="p-5 mb-6 border border-gray-200 rounded-2xl dark:border-gray-800 lg:p-6">
        <div className="gap-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex flex-row w-full">
            <h4 className="flex-1/3 text-lg font-semibold text-gray-800 dark:text-white/90 lg:mb-6">
              Liveness
            </h4>

            <div className="flex-1/3 w-full">
              <div className="flex w-full items-center gap-3">
                <div className="relative block h-2 w-full rounded-sm bg-gray-200 dark:bg-gray-800">
                  <div
                    className={
                      "absolute left-0 top-0 flex h-full items-center justify-center rounded-sm bg-brand-500 text-xs font-medium text-white"
                    }
                    style={{ width: data.passiveLiveness?.score + "%" }}
                  ></div>
                </div>
                <p className="text-theme-sm font-medium text-gray-800 dark:text-white/90">
                  {data.passiveLiveness?.score}%
                </p>
              </div>
            </div>

            <div className="flex-1/3 text-right">
              <VerificationStatus status={data.passiveLiveness?.status} />
            </div>
          </div>

          <div className="flex flex-row w-full mr-4">
            <div className="flex-1/3 w-full mr-5">
              <div className="p-3 mb-4 border border-gray-300 dark:border-white rounded-2xl">
                <img alt="ID Back" src={contentUrl + data.selfieImageUrl} />
              </div>
            </div>

            <div className="flex-1/3 w-full">
              <div>
                <div className="border rounded-2xl border-gray-300 mb-6 p-5 dark:border-white">
                  <p className="mt-5 text-xs leading-normal text-gray-500 dark:text-gray-400">
                    Age
                  </p>
                  <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                    <span className="inline-flex items-center justify-center gap-1 rounded-full bg-gray-800 px-2.5 py-0.5 text-sm font-medium text-white dark:bg-white/15 dark:text-white">
                      {Math.round(
                        data.passiveLiveness?.user_image?.entities[0]?.age,
                      )}
                    </span>
                  </p>
                </div>
              </div>

              {data.faceMatch?.warnings.length > 0 && (
                <div>
                  <p className="mt-5 text-xs leading-normal text-gray-500 dark:text-gray-400">
                    Warnings
                  </p>
                  <div className="text-sm font-medium text-gray-800 dark:text-white/90">
                    {data.faceMatch?.warnings.map((w, ndx) => (
                      <p key={ndx}>{w.short_description}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* AML */}

      <div className="p-5 mb-6 border border-gray-200 rounded-2xl dark:border-gray-800 lg:p-6">
        <div className="gap-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex flex-row w-full">
            <h4 className="flex-1/3 text-lg font-semibold text-gray-800 dark:text-white/90 lg:mb-6">
              AML
            </h4>

            <div className="flex-1/3 w-full">
              <div className="flex w-full items-center gap-3">
                <div className="relative block h-2 w-full rounded-sm bg-gray-200 dark:bg-gray-800">
                  <div
                    className={
                      "absolute left-0 top-0 flex h-full items-center justify-center rounded-sm bg-brand-500 text-xs font-medium text-white"
                    }
                    style={{ width: data.aml?.score + "%" }}
                  ></div>
                </div>
                <p className="text-theme-sm font-medium text-gray-800 dark:text-white/90">
                  {data.aml?.score}%
                </p>
              </div>
            </div>

            <div className="flex-1/3 text-right">
              <VerificationStatus status={data.aml?.status} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-4 lg:gap-7 2xl:gap-x-32">
            <div>
              <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                Document Number
              </p>
              <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                {print(data.aml?.screened_data?.document_number)}
              </p>
            </div>

            <div>
              <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                Full Name
              </p>
              <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                {print(data.aml?.screened_data?.full_name)}
              </p>
            </div>

            <div>
              <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                Date of Birth
              </p>
              <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                {print(data.aml?.screened_data?.date_of_birth)}
              </p>
            </div>

            <div>
              <p className="mb-2 text-xs leading-normal text-gray-500 dark:text-gray-400">
                Nationality
              </p>
              <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                {print(data.aml?.screened_data?.nationality)}
              </p>
            </div>
          </div>
        </div>
      </div>

      <Modal isOpen={isModalOpen} onClose={handleClose} title=" ">
            {modalContent}
          </Modal>
    </>
  );
}
